// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@flip/App/Components/Common/SectionWrapper";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { GridContainerProps } from "@flip/MobiusOverrides/GridContainer/GridContainer";
import Linkedin from "@flip/XDL/Icons/Linkedin";
import { color, colorText, xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    teamMembers = "teamMembers",
}

const enum teamMembersFields {
    name = "name",
    image = "image",
    jobTitle = "jobTitle",
    linkedInProfile = "linkedInProfile",
    description = "description",
}

interface TeamMember {
    fields: {
        name: string;
        image: string;
        jobTitle: string;
        linkedInProfile: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.teamMembers]: TeamMember[];
    };
}

type Props = OwnProps;

export interface OurTeamStyles {
    container?: GridContainerProps;
    memberCardGridItem?: GridItemProps;
    memberCard?: InjectableCss;
    memberCardInfo?: InjectableCss;
    image?: LazyImageProps;
    name?: TypographyProps;
    jobTitle?: TypographyProps;
    description?: TypographyProps;
    memberDetails?: InjectableCss;
    detailsImage?: LazyImageProps;
    memberDetailsInfo?: InjectableCss;
    link?: LinkPresentationProps;
    linkedInIcon?: IconPresentationProps;
}

export const ourTeamStyles: OurTeamStyles = {
    container: {
        gap: 22,
        offsetCss: css`
            justify-content: center;
        `,
    },
    memberCardGridItem: {
        width: [6, 3, 3, 2, 2],
        css: css``,
    },
    memberCard: {
        css: css`
            display: flex;
            cursor: pointer;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 0 4px 20px 4px;
            height: 100%;
            width: 100%;

            ${({ isActive }: any) => {
                return isActive
                    ? css`
                          background: linear-gradient(
                              to bottom,
                              transparent 0,
                              transparent 20px,
                              ${color("secondary.main")} 20px,
                              ${color("secondary.main")} 100%
                          );
                      `
                    : css`
                          background: linear-gradient(
                              to bottom,
                              transparent 0,
                              transparent 20px,
                              ${color("common.ui2")} 20px,
                              ${color("common.ui2")} 100%
                          );
                      `;
            }}
        `,
    },
    memberCardInfo: {
        css: css``,
    },
    image: {
        css: css`
            object-fit: cover;
            width: 166px;
            height: 166px;
            margin-bottom: 18px;

            ${xScreen("lt", "md")} {
                height: 150px;
                width: 150px;
            }
        `,
    },
    name: {
        css: css`
            margin-bottom: 8px;
            text-align: center;
            display: inline-block;
        `,
    },
    jobTitle: {
        variant: "p",
        css: css`
            margin-bottom: 0;
            text-align: center;
        `,
    },
    description: {
        variant: "p",
        size: "14px",
        css: css`
            margin-bottom: 0;
            text-align: start;
        `,
    },
    memberDetails: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            border-bottom: 1px solid ${color("common.border")};
            padding: 24px 0;
            display: flex;
            align-items: center;
            width: 100%;

            ${xScreen("lt", "md")} {
                align-items: flex-start;
            }

            ${xScreen("lt", "sm")} {
                flex-direction: column;
                align-items: center;
            }
        `,
    },
    detailsImage: {
        css: css`
            object-fit: cover;
            width: 298px;
            height: 298px;
            flex: 0 0 298px;

            ${xScreen("lt", "md")} {
                margin-bottom: 12px;
            }
        `,
    },
    memberDetailsInfo: {
        css: css`
            margin-left: 24px;
            h6,
            p {
                text-align: left;
            }
        `,
    },
    link: {
        css: css``,
    },
    linkedInIcon: {
        css: css``,
    },
};

const styles = ourTeamStyles;

export const OurTeam: React.FC<Props> = ({ fields }) => {
    const [activeMember, setActiveMember] = React.useState<number | undefined>();
    const { isTabletScreen, isSmallMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isLargeTabletScreen,
        isSmallMobileScreen: (state as CustomAppState).xCommon.isSmallMobileScreen,
    }));
    const itemsPerRow = isSmallMobileScreen ? 2 : isTabletScreen ? 4 : 6;
    const onTeamMemberClick = (index: number) => {
        activeMember === index ? setActiveMember(undefined) : setActiveMember(index);
    };

    return (
        <SectionWrapper title={colorText(fields.title)} data-test-selector="ourTeam">
            <GridContainer {...styles.container}>
                {fields.teamMembers.map((member, index) => {
                    return (
                        <GridItem
                            key={member.fields.name}
                            onClick={() => onTeamMemberClick(index)}
                            {...styles.memberCardGridItem}
                            style={{ order: index }}
                        >
                            <StyledWrapper {...styles.memberCard} {...{ isActive: index === activeMember }}>
                                <LazyImage src={member.fields.image} altText={member.fields.name} {...styles.image} />
                                <StyledWrapper {...styles.memberCardInfo}>
                                    <Typography variant="h6" {...styles.name}>
                                        {member.fields.name}
                                    </Typography>
                                    <Typography size="14px" {...styles.jobTitle}>
                                        {member.fields.jobTitle}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    );
                })}
                {activeMember !== undefined && (
                    <GridItem
                        width={12}
                        style={{ order: activeMember + (itemsPerRow - (activeMember % itemsPerRow)) - 1 }}
                    >
                        <StyledWrapper {...styles.memberDetails}>
                            <LazyImage
                                src={fields.teamMembers[activeMember].fields.image}
                                altText={fields.teamMembers[activeMember].fields.name}
                                {...styles.detailsImage}
                            />
                            <StyledWrapper {...styles.memberDetailsInfo}>
                                <Typography variant="h3" {...styles.name}>
                                    {fields.teamMembers[activeMember].fields.name}
                                </Typography>
                                {fields.teamMembers[activeMember].fields.linkedInProfile && (
                                    <Link
                                        {...styles.link}
                                        href={fields.teamMembers[activeMember].fields.linkedInProfile}
                                        target="_blank"
                                        icon={{
                                            iconProps: {
                                                size: 16,
                                                color: "primary",
                                                css: css`
                                                    margin-left: 24px;
                                                `,
                                                src: Linkedin,
                                            },
                                        }}
                                    />
                                )}
                                <Typography lineHeight="26px" {...styles.jobTitle}>
                                    {fields.teamMembers[activeMember].fields.jobTitle}
                                </Typography>
                                <Typography {...styles.description} style={{ marginTop: "20px" }}>
                                    {fields.teamMembers[activeMember].fields.description}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OurTeam,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.teamMembers,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: teamMembersFields.name,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.jobTitle,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.linkedInProfile,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: teamMembersFields.description,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
